import { InputWithLabel } from '@adotel/components'
import {
  ApolloQueryResult,
  OperationVariables,
  useMutation
} from '@apollo/client'
import {
  Typography,
  Grid,
  Switch,
  IconButton,
  Button,
  CircularProgress,
  Hidden
} from '@material-ui/core'
import RightIcon from '@material-ui/icons/ArrowRightAlt'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import VisibilityIcon from '@material-ui/icons/Visibility'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff'
import { useState } from 'react'
import { toast } from 'react-toastify'

import {
  CREATE_PMS_MAPPING_FOR_CHANNEL_MUTATION,
  MODIFY_PMS_MAPPING_FOR_CHANNEL_MUTATION
} from '../../graphql/accommodationPMSMapping'
import { persianRegex } from '../../helpers/regex'
import { getTranslate } from '../../localization'
import {
  PMSMappingForChannel,
  PMSMappingListForChannel
} from '../../types/accommodationPMSMapping'
import useStyles from './styles/index.styles'

interface ChannelDetailsContainerProps {
  channelName: string
  loadingForGetPropertyCode: boolean
  loadingForPMSMappingsAccommodationList: boolean
  isExpanded: boolean
  index: number
  onClickOnExpandIcon: (index: number) => void
  refetchForChannelPMSMappingList: (
    variables?: Partial<OperationVariables> | undefined
  ) => Promise<
    ApolloQueryResult<{
      pmsMappingListForChannel: PMSMappingListForChannel[]
    }>
  >
  loadingForPMSMappingsChannelList: boolean
  PMSMappingsForChannelsData: PMSMappingListForChannel[]
  setPMSMappingsForChannelsData: React.Dispatch<
    React.SetStateAction<PMSMappingListForChannel[]>
  >
  PMSMappingForChannelInput: PMSMappingForChannel
}

export const ChannelDetailsContainer: React.FC<
  ChannelDetailsContainerProps
> = ({
  channelName,
  loadingForGetPropertyCode,
  loadingForPMSMappingsAccommodationList,
  isExpanded,
  index,
  onClickOnExpandIcon,
  refetchForChannelPMSMappingList,
  loadingForPMSMappingsChannelList,
  PMSMappingsForChannelsData,
  setPMSMappingsForChannelsData,
  PMSMappingForChannelInput
}) => {
  const classes = useStyles()
  const [visibilityIconClicked, setVisibilityIconClicked] = useState(false)

  const [
    createPMSMappingForChannel,
    { loading: loadingForCreatePMSMappingChannel }
  ] = useMutation(CREATE_PMS_MAPPING_FOR_CHANNEL_MUTATION, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      refetchForChannelPMSMappingList?.()
      toast.success(data.createPMSMappingForChannel.message)
    },
    onError: (error) => {
      toast.error(error.message)
    }
  })

  const [
    modifyPMSMappingForChannel,
    { loading: loadingForModifyPMSMappingChannel }
  ] = useMutation(MODIFY_PMS_MAPPING_FOR_CHANNEL_MUTATION, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      refetchForChannelPMSMappingList?.()
      toast.success(data.modifyPMSMappingForChannel.message)
    },
    onError: (error) => {
      toast.error(error.message)
    }
  })

  const clickOnShowPassword = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation()
    setVisibilityIconClicked(!visibilityIconClicked)
  }

  const onClickForChangePasswordOrPmscode = () => {
    if (PMSMappingsForChannelsData[index].exist) {
      modifyPMSMappingForChannel({
        variables: {
          input: PMSMappingForChannelInput
        }
      })
    } else {
      createPMSMappingForChannel({
        variables: {
          input: PMSMappingForChannelInput
        }
      })
    }
  }

  const isPersian = persianRegex.test(channelName)

  return (
    <div className={classes.accommodationDetailsContainerForChannel}>
      <div>
        <div className={classes.itemBox}>
          <Typography
            variant="h4"
            className={classes.title}
            style={isPersian ? { fontFamily: 'IranSans' } : undefined}>
            {channelName}
          </Typography>
          <div className={classes.codeMappingContianer}>
            <div>
              <InputWithLabel
                noHelperText
                label={getTranslate('Adotel Code')}
                viewMode
                value={PMSMappingsForChannelsData[index].AdotelCode}
                inputSize="small"
              />
            </div>
            <Hidden smDown>
              <RightIcon />
            </Hidden>
            <div className={classes.mappedCodeContainer}>
              <InputWithLabel
                value={PMSMappingsForChannelsData[index].PMSCode}
                noHelperText
                label={getTranslate('Mapped Code')}
                inputSize="small"
                onChange={(event) => {
                  setPMSMappingsForChannelsData([
                    ...PMSMappingsForChannelsData.slice(0, index),
                    {
                      ...PMSMappingsForChannelsData[index],
                      PMSCode: event.currentTarget.value
                    },
                    ...PMSMappingsForChannelsData.slice(index + 1)
                  ])
                }}
                error={!PMSMappingsForChannelsData[index].PMSCode}
                autoComplete="new-password"
              />
            </div>
          </div>
        </div>
      </div>
      <div className={classes.pmsPasswordContainer}>
        <InputWithLabel
          label={getTranslate('PMS password')}
          endIcon={
            <IconButton onClick={(event) => clickOnShowPassword(event)}>
              {!visibilityIconClicked ? (
                <VisibilityOffIcon />
              ) : (
                <VisibilityIcon />
              )}
            </IconButton>
          }
          inputSize="small"
          placeholder={getTranslate('Enter password')}
          autoComplete="new-password"
          type={visibilityIconClicked ? 'text' : 'password'}
          value={PMSMappingsForChannelsData[index].PMSPass}
          fullWidth
          onChange={(event) => {
            setPMSMappingsForChannelsData([
              ...PMSMappingsForChannelsData.slice(0, index),
              {
                ...PMSMappingsForChannelsData[index],
                PMSPass: event.currentTarget.value
              },
              ...PMSMappingsForChannelsData.slice(index + 1)
            ])
          }}
        />
      </div>
      <div className={classes.switchContainerActive}>
        <Typography variant="h4">{getTranslate('Active')}</Typography>
        <Typography component="div" className={classes.switch}>
          <Grid
            component="label"
            container
            alignItems="center"
            justify="flex-start">
            <Grid item>{getTranslate('No')}</Grid>
            <Grid item>
              <Switch
                name="active"
                color="primary"
                onChange={() => {
                  // setHaveSuppurtRatePlans(!haveSuppurtRatePlans)
                }}
                checked={
                  // customizedForChannel
                  //   ? selectedChannel?.Active
                  //   : PMSMappingsAccommodationList?.Active
                  true
                }
                disabled={true}
              />
            </Grid>
            <Grid item>{getTranslate('Yes')}</Grid>
          </Grid>
        </Typography>
      </div>
      <div className={classes.saveChangesButtonContainer}>
        <Button
          variant="contained"
          color="primary"
          disabled={
            loadingForPMSMappingsAccommodationList ||
            loadingForPMSMappingsChannelList ||
            loadingForGetPropertyCode ||
            loadingForCreatePMSMappingChannel ||
            loadingForModifyPMSMappingChannel
          }
          onClick={onClickForChangePasswordOrPmscode}>
          {getTranslate('Save Changes')}
          {(loadingForCreatePMSMappingChannel ||
            loadingForModifyPMSMappingChannel) && (
            <CircularProgress className={classes.circularProgress} size={16} />
          )}
        </Button>
      </div>
      <div className={classes.dropDownIconContainer}>
        <IconButton
          onClick={() => {
            onClickOnExpandIcon(index)
          }}>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </div>
    </div>
  )
}
